/**
 * AddSinglePromoterModal.tsx - Single Promoter Addition Modal
 *
 * This component provides an interface for adding individual promoters to
 * fundraisers with contact search, selection, and goal setting capabilities.
 *
 * Key Features:
 * - Contact search and selection interface
 * - New contact creation with form validation
 * - Promoter goal setting and configuration
 * - Contact filtering by type, lists, interests, and tags
 * - Form validation with error handling
 * - Responsive modal design
 *
 * Contact Management:
 * - Search existing contacts by name, email, or phone
 * - Filter contacts by categories and attributes
 * - Create new contacts with complete information
 * - Contact validation and duplicate detection
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - onPromoterAdded?: (promoter: Promoter) => void - Success callback
 *
 * Dependencies: Contact data, form validation, portal rendering
 */

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X, Search, Check, Edit, User } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { Toast } from "./Toast";
import {
  ClearButton,
  PrimaryButton,
  SecondaryButton,
} from "./shared/StyledComponents";
import { GoalSettingModal } from "./shared/GoalSettingModal";
import { formatPhoneNumber, isValidEmail } from "../utils/formatters";
import { IAPIJoinFundRaiserRequest, IPromoter } from "../interfaces";
import {
  ContactReferenceModel,
  ContactService,
  CountryDropdown,
  CountryPhoneDropdown,
  CountryWithStateModel,
  IAPICreateContactModel,
  TaxStatus,
} from "@mindmemobile/mm-payment-modules";
import { count } from "console";
import {
  CreatePromoterContactModel,
  DonorInfo,
  PromoterProgressModel,
} from "../models";
import { set } from "lodash";
import { FundraiserService } from "../services";
import { firstValueFrom } from "rxjs";
import ToastService from "./ToastService";

interface IFormInputs{
  hasError?: boolean;
}

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
  padding: 20px;
  overflow-y: auto;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 90%;
  max-width: 700px;
  height: 650px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 40px 40px 16px;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ToggleButtons = styled.div`
  display: flex;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.active
      ? `
    background: #ffffff;
    color: #1e293b;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `
      : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      color: #1e293b;
      background: rgba(255, 255, 255, 0.5);
    }
  `}
`;

const ContentContainer = styled.div`
  padding: 16px 40px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #f1f5f9;
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 0 0 3px transparent;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  width: 20px;
  height: 20px;
`;

const ContactsList = styled.div<{ hasContacts: boolean }>`
  height: 350px;
  overflow-y: ${(props) => (props.hasContacts ? "auto" : "hidden")};
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  flex: 1;
  background: #ffffff;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const ContactItem = styled.button<{ selected: boolean }>`
  width: 100%;
  padding: 16px;
  border: none;
  background: ${(props) => (props.selected ? "#f0f9ff" : "#ffffff")};
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${(props) => (props.selected ? "#e0f2fe" : "#f8fafc")};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ContactContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContactName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const ContactDetails = styled.div`
  font-size: 14px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SelectionIndicator = styled.div<{ visible: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${(props) => (props.visible ? "#10b981" : "transparent")};
  border: 2px solid ${(props) => (props.visible ? "#10b981" : "#e2e8f0")};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  overflow-y: auto;
  max-height: 350px;
  padding-right: 8px;
  margin-right: -8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FormGroup = styled.div<IFormInputs>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  .country-dropdown-expanded {
    .ps-country-dpdn {
      > div {
        padding: 12px 16px;
        border: 2px solid #e2e8f0;
        border-radius: 8px;
        font-size: 16px;
        color: #1e293b;
        border-color: ${({ hasError }) =>
          hasError ? "#ef4444" : "#e2e8f0"}; // red if error
        box-shadow: 0 0 0 3px transparent;
        width: 100%;

        &:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        &::placeholder {
          color: #9ca3af;
        }
        input {
          width: 100%;
        }
      }
      .dropdown-menu {
        transform: translate(-18px, -40px) !important;
      }
      .styled-dropdown-flex .custom-label {
        font-size: 16px;
        color: #1e293b;
      }
      .input-wrapper {
        input {
          color: #1e293b;
          font-size: 16px;
        }
      }
    }
  }
`;

const FormLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;

  &.required::after {
    content: " *";
    color: #dc2626;
  }

  /* Add padding above specific labels */
  &#email-label,
  &#phone-label {
    margin-top: 10px;
  }

  &#mobile-label {
    margin-top: 10px;
  }

  &#country-label {
    margin-top: 10px;
  }
`;
interface InputProps {
  hasError?: boolean;
}

const FormInput = styled.input<InputProps>`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  border-color: ${({ hasError }) =>
    hasError ? "#ef4444" : "#e2e8f0"}; // red if error
  box-shadow: 0 0 0 3px transparent;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PhoneInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PhonePrefix = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  z-index: 1;
`;

const PhoneInput = styled.input<{ hasError: boolean }>`
  padding: 12px 16px 12px 48px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#10b981")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(16, 185, 129, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CountrySelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  box-shadow: 0 0 0 3px transparent;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const ModalFooter = styled.div`
  padding: 16px 40px 32px;
  flex-shrink: 0;
`;

const GoalSection = styled.div`
  margin-bottom: 34px;
  margin-top: -10px;
`;

const GoalCaption = styled.div`
  text-align: left;
  font-size: 14px;
  margin-bottom: 16px;
  padding-bottom: 10px;

  &.no-goal {
    color: #dc2626;
  }

  &.has-goal {
    color: #64748b;
  }
`;

const GoalLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-size: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const EmptyStateIcon = styled.div`
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateContent = styled.div`
  display: flex;
  align-items: center;
`;

const EmptyStateTitle = styled.h4`
  font-size: 16px;
  font-weight: 500;
  color: #1e293b;
  margin: 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const SelectionCounter = styled.div`
  padding: 8px 0;
  font-size: 14px;
  color: #64748b;
  text-align: left;
  flex-shrink: 0;
`;

type ViewMode = "existing" | "new";

interface AddSinglePromoterModalProps {
  isVisible: boolean;
  fromDetailPage?: boolean;
  onClose: () => void;
  fundraiserId: string;
  promoters: PromoterProgressModel[];
  updatePromoters: (promoters: PromoterProgressModel[]) => void;
  onSelect: (contacts: ContactReferenceModel[]) => void;
}

export const AddSinglePromoterModal: React.FC<AddSinglePromoterModalProps> = ({
  isVisible,
  fromDetailPage,
  onClose,
  fundraiserId,
  promoters,
  onSelect,
  updatePromoters,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("existing");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContacts, setSelectedContacts] = useState<
    ContactReferenceModel[]
  >([]);
  const [showGoalModal, setShowGoalModal] = useState(false);

  const countries = fundraiserStore.countryStore.countriesWithState;
  const usCountry = countries.find((c) => c.getUSCountry());
  const defaultFormData = new CreatePromoterContactModel({
    countryId: usCountry?.id || "",
    countryCode: usCountry?.code?.toString() || "",
  });

  // Form state for new contact
  const [formData, setFormData] =
    useState<CreatePromoterContactModel>(defaultFormData);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  // Reset state when modal opens
  useEffect(() => {
    if (isVisible) {
      setViewMode("existing");
      setSearchTerm("");
      setSelectedContacts([]);
      setFormData(defaultFormData);
    }
  }, [isVisible]);

  const filteredContacts =
    searchTerm.trim() === ""
      ? []
      : fundraiserStore.contacts.filter((contact) => {
          const searchLower = searchTerm.toLowerCase();
          const isValid =
            contact.firstName.toLowerCase().includes(searchLower) ||
            contact.lastName.toLowerCase().includes(searchLower) ||
            contact.email?.toLowerCase()?.includes?.(searchLower) ||
            contact.workEmail?.toLowerCase()?.includes?.(searchLower) ||
            contact.otherEmail?.toLowerCase()?.includes?.(searchLower) ||
            contact.mobilePhone?.phoneNumber?.includes?.(searchTerm) ||
            contact.homePhone?.phoneNumber?.includes?.(searchTerm) ||
            contact.workPhone?.phoneNumber?.includes?.(searchTerm) ||
            contact.otherPhone?.phoneNumber?.includes?.(searchTerm);
          return isValid === true;
        });

  const clearSearch = () => {
    setSearchTerm("");
    setSelectedContacts([]);
  };

  const handleContactSelect = (contact: ContactReferenceModel) => {
    const newSelected = [...selectedContacts];
    if (newSelected.includes(contact)) {
      newSelected.splice(newSelected.indexOf(contact), 1);
    } else {
      newSelected.push(contact);
    }
    setSelectedContacts(newSelected);
  };

  const handleAddExistingContacts = () => {
    if (selectedContacts.length === 0) return;

    const contactsToAdd = fundraiserStore.contacts.filter((contact) =>
      selectedContacts.includes(contact)
    );

    // Check for existing promoters by email
    const existingEmails = new Set(
      promoters
        .filter((p) => p.email && p.fundraiserId === fundraiserId)
        .map((p) => p.email.toLowerCase())
    );

    const uniqueContacts = contactsToAdd.filter(
      (contact) => !existingEmails.has(contact.displayEmail.toLowerCase())
    );

    if (uniqueContacts.length === 0) {
      const message =
        selectedContacts.length === 1
          ? `Selected contact is already promoter.`
          : `All selected contacts are already promoters`;
      ToastService.showError(message);
      onClose();
      return;
    }

    if (fromDetailPage) {
      onSelect(uniqueContacts);
      return;
    }

    // Add contacts as promoters
    const newPromoters: PromoterProgressModel[] = uniqueContacts.map(
      (contact) => {
        return new PromoterProgressModel({
          id: contact.id,
          contactId: contact.id,
          mindmeContactId: contact.mindmeContactId,
          firstName: contact.firstName,
          lastName: contact.lastName,
          displayName: `${contact.firstName} ${contact.lastName}`,
          email: contact.displayEmail,
          mobile: contact.displayMobileNumber,
          totalRaisedAmount: 0,
          goalAmount: fundraiserStore.getDefaultPromoterGoal(fundraiserId),
          rank:
            promoters.filter((p) => p.fundraiserId === fundraiserId).length + 1,
          imageURL: "", // Use empty string so initials will be shown
          fundraiserId: fundraiserId,
          isActive: true,
          totalDonations: 0,
          isNew: true,
        });
      }
    );

    updatePromoters([...promoters, ...newPromoters]);
    ToastService.showSuccess(
      `${uniqueContacts.length} promoter${uniqueContacts.length !== 1 ? "s" : ""} added successfully`
    );
    setSelectedContacts([]);
    onClose();
  };

  const createNewPromoter = (
    contactId: string,
    mindmeContactId: string
  ): PromoterProgressModel => {
    const newPromoter: PromoterProgressModel = {
      id: contactId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      displayName: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      mobile: formData.mobile,
      countryId: formData.countryId || "",
      countryCode: formData.countryCode || "",
      contactId: contactId, // Generate unique contactId for new contacts
      mindmeContactId: mindmeContactId || "",
      totalRaisedAmount: 0,
      goalAmount: fundraiserStore.getDefaultPromoterGoal(fundraiserId),
      rank: promoters.filter((p) => p.fundraiserId === fundraiserId).length + 1,
      imageURL: "", // Use empty string so initials will be shown
      fundraiserId: fundraiserId,
      isActive: true,
      totalDonations: 0,
      isNew: true,
    };
    return newPromoter;
  };

  const handleCreateNewContact = async () => {
    if (!formData.validate()) {
      setFormData((prev) => {
        const updatedInfo = new CreatePromoterContactModel({
          ...prev,
        });
        return updatedInfo;
      });
      return;
    }

    // Check if email matches existing contact
    const existingContact = fundraiserStore.contacts.find(
      (contact) => contact.email.toLowerCase() === formData.email.toLowerCase()
    );

    if (existingContact) {
      // Check if this contact is already a promoter
      const existingPromoter = promoters.find(
        (p) =>
          p.email.toLowerCase() === formData.email.toLowerCase() &&
          p.fundraiserId === fundraiserId
      );

      if (existingPromoter) {
        ToastService.showError("This contact is already a promoter");
        onClose();
        return;
      }
      const newContacts = fundraiserStore.contacts.filter(
        (contact) =>
          promoters.some((p) => p.contactId === contact.id) ||
          existingContact.id === contact.id
      );

      onSelect(newContacts);
      onClose();
    } else {
      const existingPromoter = promoters.find(
        (p) =>
          p.email.toLowerCase() === formData.email.toLowerCase() &&
          p.fundraiserId === fundraiserId
      );

      if (existingPromoter) {
        ToastService.showError("A promoter with this email already exists");
        onClose();
        return;
      }

      if (fundraiserId) {
        const addPromoterRequest = CreatePromoterContactModel.createAPIRequest(
          formData,
          fundraiserId
        );
        const response = await firstValueFrom(
          FundraiserService.addNewPromoterToFundRaiser(addPromoterRequest)
        );
        if (response.result?.contactId) {
          fundraiserStore.fetchMindMeContacts();
          const newPromoter = createNewPromoter(
            response.result.contactId,
            response.result?.mindmeContactId || ""
          );
          updatePromoters([...promoters, newPromoter]);
          ToastService.showSuccess("New contact created successfully.");
          onClose();
        }
        onClose();
        return;
      }
      const createContactRequest =
        CreatePromoterContactModel.createAPIRequest2(formData);
      const response = await firstValueFrom(
        ContactService.createContact(createContactRequest)
      );
      if (response.result?.id) {
        const newPromoter = createNewPromoter(
          response.result?.id,
          response.result?.mindmeContactId || ""
        );
        updatePromoters([...promoters, newPromoter]);
      }
      onClose();
      if (response.result?.isEmailExist) {
        ToastService.showSuccess(
          "This contact already exists and has been added to promoters successfully."
        );
      } else if (response.result?.id) {
        ToastService.showSuccess("New contact created successfully.");
      }
    }
  };

  const updateFormInfo = (
    field: keyof CreatePromoterContactModel,
    value: string | boolean | any
  ) => {
    setFormData((prev) => {
      const updatedInfo = new CreatePromoterContactModel({
        ...prev,
        [field]: value,
      });
      updatedInfo.validate(true);
      return updatedInfo;
    });
  };

  const handleGoalSave = (amount: number) => {
    fundraiserStore.updateBuilderField("promoterGoal", amount);
  };

  // Check if fundraiser has a goal
  const fundraiserGoal = fundraiserStore.builderData?.goal || 0;
  const hasFundraiserGoal = fundraiserGoal > 0;

  const isExistingContactValid = selectedContacts.length > 0;
  const formatGoalDisplay = () => {
    if (!hasFundraiserGoal) {
      return "no goal since the fundraiser has no goal";
    }

    const goal = fundraiserStore.builderData?.promoterGoal;
    if (!goal || goal === 0) {
      return "no goal";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(goal);
  };

  if (!isVisible) return null;

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalHeader>
          <ModalTitle>Add Single Promoter</ModalTitle>
          <ToggleButtons>
            <ToggleButton
              active={viewMode === "existing"}
              onClick={() => setViewMode("existing")}
            >
              Select Existing Contact
            </ToggleButton>
            <ToggleButton
              active={viewMode === "new"}
              onClick={() => setViewMode("new")}
            >
              Create New Contact
            </ToggleButton>
          </ToggleButtons>
        </ModalHeader>

        <ContentContainer>
          {viewMode === "existing" ? (
            <SearchSection>
              <SearchWrapper>
                <SearchIcon />
                <SearchInput
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <ClearButton onClick={clearSearch}>
                    <X size={16} />
                  </ClearButton>
                )}
              </SearchWrapper>

              <ContactsList
                hasContacts={
                  searchTerm.trim() !== "" && filteredContacts.length > 0
                }
              >
                {searchTerm.trim() === "" ? (
                  <EmptyState>
                    <EmptyStateContent>
                      <EmptyStateTitle>
                        Start typing in the search bar
                      </EmptyStateTitle>
                    </EmptyStateContent>
                  </EmptyState>
                ) : filteredContacts.length === 0 ? (
                  <EmptyState>
                    <EmptyStateContent>
                      <EmptyStateTitle>No matching contacts</EmptyStateTitle>
                    </EmptyStateContent>
                  </EmptyState>
                ) : (
                  filteredContacts.map((contact, idx) => (
                    <ContactItem
                      key={contact.id || idx}
                      selected={selectedContacts.includes(contact)}
                      onClick={() => handleContactSelect(contact)}
                    >
                      <ContactContent>
                        <ContactName>
                          {contact.firstName} {contact.lastName}
                        </ContactName>
                        <ContactDetails>
                          {contact.displayEmail && (
                            <div>{contact.displayEmail}</div>
                          )}
                          {contact.displayMobileNumber && (
                            <div>{contact.displayMobileNumber}</div>
                          )}
                        </ContactDetails>
                      </ContactContent>
                      <SelectionIndicator
                        visible={selectedContacts.includes(contact)}
                      >
                        {selectedContacts.includes(contact) && (
                          <Check size={14} />
                        )}
                      </SelectionIndicator>
                    </ContactItem>
                  ))
                )}
              </ContactsList>
            </SearchSection>
          ) : (
            <FormSection>
              <div
                style={{
                  maxHeight: "350px",
                  overflowY: "auto",
                  paddingRight: "8px",
                  marginRight: "-8px",
                }}
              >
                <FormRow>
                  <FormGroup>
                    <FormLabel htmlFor="firstName">First Name</FormLabel>
                    <FormInput
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) =>
                        updateFormInfo("firstName", e.target.value)
                      }
                      placeholder="Enter first name"
                      hasError={formData.hasError("firstName")}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="lastName">Last Name</FormLabel>
                    <FormInput
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) =>
                        updateFormInfo("lastName", e.target.value)
                      }
                      placeholder="Enter last name"
                      hasError={formData.hasError("lastName")}
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <FormLabel
                    htmlFor="email"
                    className="required"
                    id="email-label"
                  >
                    Email Address
                  </FormLabel>
                  <FormInput
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormInfo("email", e.target.value)}
                    placeholder="Enter email address"
                    hasError={formData.hasError("email")}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="phone" id="phone-label">
                    Mobile Phone
                  </FormLabel>
                  <CountryPhoneDropdown
                    selectedCountry={
                      countries.find((c) => c.id === formData.countryId) ||
                      new CountryWithStateModel()
                    }
                    error={
                      formData.hasError("mobile") ||
                      formData.hasError("countryId")
                    }
                    errorMessage={
                      formData.errors.find((e) =>
                        ["mobile", "countryId"].includes(e.field)
                      )?.message || ""
                    }
                    onCountryChange={(id: string) => {
                      const selectedCountry = countries.find(
                        (c) => c.id === id
                      );
                      updateFormInfo("countryId", selectedCountry?.id || "");
                      updateFormInfo(
                        "countryCode",
                        selectedCountry?.code?.toString() || ""
                      );
                    }}
                    phoneNumber={formData.mobile}
                    onPhoneNumberChange={(number: string) =>
                      updateFormInfo("mobile", number)
                    }
                    availableCountries={countries}
                    className="country-dropdown-expanded"
                  />
                </FormGroup>
              </div>
            </FormSection>
          )}
        </ContentContainer>

        <ModalFooter>
          {viewMode === "existing" ? (
            selectedContacts.length > 0 && (
              <GoalSection>
                <GoalCaption
                  className={hasFundraiserGoal ? "has-goal" : "no-goal"}
                >
                  {hasFundraiserGoal ? (
                    <>
                      Promoter goal will be set to{" "}
                      <GoalLink onClick={() => setShowGoalModal(true)}>
                        {formatGoalDisplay()}
                        <Edit size={14} />
                      </GoalLink>
                    </>
                  ) : (
                    <>
                      Promoter goal will be set to no goal since the fundraiser
                      has no goal
                    </>
                  )}
                </GoalCaption>
              </GoalSection>
            )
          ) : (
            <GoalSection>
              <GoalCaption
                className={hasFundraiserGoal ? "has-goal" : "no-goal"}
              >
                {hasFundraiserGoal ? (
                  <>
                    Promoter goal will be set to{" "}
                    <GoalLink onClick={() => setShowGoalModal(true)}>
                      {formatGoalDisplay()}
                      <Edit size={14} />
                    </GoalLink>
                  </>
                ) : (
                  <>
                    Promoter goal will be set to no goal since the fundraiser
                    has no goal
                  </>
                )}
              </GoalCaption>
            </GoalSection>
          )}

          <ModalActions>
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton
              disabled={
                viewMode === "existing"
                  ? !isExistingContactValid
                  : formData.errors.length > 0
              }
              onClick={
                viewMode === "existing"
                  ? handleAddExistingContacts
                  : handleCreateNewContact
              }
            >
              Add
            </PrimaryButton>
          </ModalActions>
        </ModalFooter>
      </ModalContent>

      <GoalSettingModal
        isVisible={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        onSave={handleGoalSave}
        initialGoal={fundraiserStore.builderData?.promoterGoal || 0}
        fundraiserGoal={fundraiserStore.selectedFundraiser?.goal}
        title="Set Promoter Goal"
      />
    </ModalOverlay>,
    document.body
  );
};
